'use client';

import { useRef } from 'react';

import { motion } from 'framer-motion';
import { MessageCircle, MousePointer2, Plus } from 'lucide-react';

import { cn } from '@kit/ui/utils';

import { ADMIN_DEMO_SIZE, AdminShell } from './admin-shell';
import {
  AnimatedNumber,
  CheckBadge,
  DEMO_COLORS,
  type DemoProps,
  DemoStage,
  type DemoText,
  EASE_OUT_EXPO,
  SPRING_SNAPPY,
  localize,
  useDemoTimeline,
} from './demo-kit';

/*
 * Admin calendar, padel venue: the day fills with bookings, a WhatsApp AI
 * booking lands on its own, then staff drag a booking to another court and
 * the player is notified automatically.
 */

export const CALENDAR_DEMO_SIZE = ADMIN_DEMO_SIZE;

const DURATIONS = [700, 2400, 2000, 900, 1100, 3000] as const;

const STEP = {
  fill: 1,
  whatsapp: 2,
  grab: 3,
  drag: 4,
  drop: 5,
} as const;

const TIME_W = 40;
const COL_W = 107;
const HOUR = 48;
const FIRST_HOUR = 17;
const HOURS = 6;
const COURTS = 6;
const DRAG_COLUMNS = 2;

const COPY = {
  label: {
    en: 'Animated demo of the admin calendar: bookings fill six padel courts, a booking made by the WhatsApp AI appears, and staff drag a booking to another court while the player is notified on WhatsApp.',
    ar: 'عرض متحرك لتقويم الإدارة: الحجوزات تملأ ستة ملاعب بادل، ويظهر حجز أنشأه مساعد واتساب الذكي، ثم ينقل الفريق حجزًا إلى ملعب آخر ويصل إشعار للاعب عبر واتساب.',
  },
  title: { en: 'Calendar', ar: 'التقويم' },
  date: { en: 'Thursday, 12 September', ar: 'الخميس، 12 سبتمبر' },
  newBooking: { en: 'New booking', ar: 'حجز جديد' },
  occupancy: { en: 'Occupancy', ar: 'الإشغال' },
  bookingsToday: { en: 'Bookings today', ar: 'حجوزات اليوم' },
  paid: { en: 'Paid', ar: 'مدفوع' },
  match: { en: 'Match', ar: 'مباراة' },
  coaching: { en: 'Coaching', ar: 'تدريب' },
  academy: { en: 'Academy', ar: 'أكاديمية' },
  court: { en: 'Court', ar: 'ملعب' },
  via: { en: 'WhatsApp AI', ar: 'مساعد واتساب' },
  to: { en: 'to', ar: 'إلى' },
  pm: { en: 'PM', ar: 'م' },
  moved: { en: 'Booking moved to Court 4', ar: 'تم نقل الحجز إلى ملعب 4' },
  notified: { en: 'Player notified on WhatsApp', ar: 'وصل إشعار للاعب عبر واتساب' },
} as const;

type Kind = 'match' | 'coaching' | 'academy' | 'maintenance';

interface Booking {
  id: string;
  court: number;
  start: number;
  duration: number;
  kind: Kind;
  title: DemoText;
  capacity?: string;
}

const MATCH: DemoText = { en: 'Open match', ar: 'مباراة مفتوحة' };

const BOOKINGS: Booking[] = [
  { id: 'a', court: 0, start: 17, duration: 1.5, kind: 'coaching', title: { en: 'Coach Faisal', ar: 'المدرب فيصل' } },
  { id: 'b', court: 0, start: 19, duration: 1.5, kind: 'match', title: MATCH },
  { id: 'c', court: 0, start: 21, duration: 1.5, kind: 'match', title: { en: 'Private · Nasser', ar: 'خاص · ناصر' } },
  { id: 'd', court: 1, start: 17, duration: 1, kind: 'match', title: MATCH },
  { id: 'e', court: 1, start: 21, duration: 1, kind: 'academy', title: { en: 'Juniors U14', ar: 'ناشئون تحت 14' }, capacity: '6/8' },
  { id: 'f', court: 2, start: 17, duration: 2, kind: 'academy', title: { en: 'Academy U12', ar: 'أكاديمية تحت 12' }, capacity: '8/10' },
  { id: 'g', court: 2, start: 19.5, duration: 1.5, kind: 'coaching', title: { en: 'Coach Dana', ar: 'المدربة دانة' } },
  { id: 'h', court: 2, start: 21.5, duration: 1.5, kind: 'match', title: MATCH },
  { id: 'i', court: 3, start: 17, duration: 1.5, kind: 'match', title: { en: 'Private · Hana', ar: 'خاص · هناء' } },
  { id: 'j', court: 3, start: 20.5, duration: 1.5, kind: 'match', title: MATCH },
  { id: 'k', court: 4, start: 17, duration: 1, kind: 'maintenance', title: { en: 'Maintenance', ar: 'صيانة' } },
  { id: 'l', court: 4, start: 18, duration: 1.5, kind: 'match', title: MATCH },
  { id: 'm', court: 4, start: 22, duration: 1, kind: 'coaching', title: { en: 'Coach Rami', ar: 'المدرب رامي' } },
  { id: 'n', court: 5, start: 17.5, duration: 1.5, kind: 'match', title: MATCH },
  { id: 'o', court: 5, start: 19.5, duration: 1.5, kind: 'coaching', title: { en: 'Coach Faisal', ar: 'المدرب فيصل' } },
  { id: 'p', court: 5, start: 21, duration: 1.5, kind: 'academy', title: { en: 'Ladies clinic', ar: 'حصة السيدات' }, capacity: '4/4' },
];

const WHATSAPP_BOOKING: Booking = {
  id: 'wa',
  court: 4,
  start: 20,
  duration: 1.5,
  kind: 'match',
  title: { en: 'Omar K.', ar: 'عمر ك.' },
};

const DRAGGED: Booking = {
  id: 'drag',
  court: 1,
  start: 18.5,
  duration: 1.5,
  kind: 'match',
  title: { en: 'Private · Khalid', ar: 'خاص · خالد' },
};

const KIND_STYLE: Record<Kind, { block: string; bar: string }> = {
  match: { block: 'bg-[#e6f0ff] text-[#202020]', bar: '#7fa6e8' },
  coaching: { block: 'bg-[#f4efe8] text-[#202020]', bar: '#b4532a' },
  academy: { block: 'bg-[#ddf5ec] text-[#1f5a48]', bar: DEMO_COLORS.success },
  maintenance: { block: 'text-[#8a857c]', bar: '#c9c3b8' },
};

const HATCH =
  'repeating-linear-gradient(135deg, #f3f1ed 0px, #f3f1ed 6px, #e9e5df 6px, #e9e5df 12px)';

const KPI = {
  occupancy: [0, 72, 78, 78, 78, 78],
  bookings: [0, 44, 45, 45, 45, 45],
  paid: [0, 92, 93, 93, 93, 93],
} as const;

const NOW_TOP = [90, 95, 100, 104, 107, 111] as const;

function clock(hour: number) {
  const whole = Math.floor(hour);
  const minutes = hour % 1 ? '30' : '00';

  return `${whole > 12 ? whole - 12 : whole}:${minutes}`;
}

export function CalendarDemo({ locale, className }: DemoProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const timeline = useDemoTimeline(stageRef, DURATIONS);
  const copy = localize(COPY, locale);
  const step = timeline.step;
  const dirSign = locale === 'ar' ? -1 : 1;
  const filled = step >= STEP.fill;

  return (
    <DemoStage ref={stageRef} timeline={timeline} label={copy.label} className={className}>
      <AdminShell
        active="calendar"
        title={copy.title}
        subtitle={copy.date}
        locale={locale}
        actions={
          <span className="flex h-7 items-center gap-1 rounded-full bg-[#202020] px-3 text-[10.5px] font-medium text-white">
            <Plus className="h-3 w-3" />
            {copy.newBooking}
          </span>
        }
      >
        <div className="px-3 pt-3">
          <div className="mb-2.5 flex h-[34px] items-center gap-2">
            <Kpi label={copy.occupancy} value={KPI.occupancy[step]!} suffix="%" />
            <Kpi label={copy.bookingsToday} value={KPI.bookings[step]!} />
            <Kpi label={copy.paid} value={KPI.paid[step]!} suffix="%" />
            <div className="ms-auto flex items-center gap-3 text-[10px] text-[#8a857c]">
              <LegendDot color={KIND_STYLE.match.bar} label={copy.match} />
              <LegendDot color={KIND_STYLE.coaching.bar} label={copy.coaching} />
              <LegendDot color={KIND_STYLE.academy.bar} label={copy.academy} />
            </div>
          </div>

          <div className="flex h-6 items-center" style={{ paddingInlineStart: TIME_W }}>
            {Array.from({ length: COURTS }, (_, index) => (
              <span
                key={index}
                className="ps-2 font-roboto-mono text-[9.5px] uppercase tracking-[0.08em] text-[#8a857c]"
                style={{ width: COL_W }}
              >
                {copy.court} {index + 1}
              </span>
            ))}
          </div>

          <div className="relative" style={{ height: HOURS * HOUR }}>
            {Array.from({ length: HOURS }, (_, index) => (
              <div
                key={index}
                className="absolute inset-x-0 border-t border-[#ebe7e1]"
                style={{ top: index * HOUR }}
              >
                <span className="absolute start-0 top-1 text-[9.5px] text-[#a8a298]">
                  {FIRST_HOUR + index - 12} {copy.pm}
                </span>
              </div>
            ))}
            {Array.from({ length: COURTS }, (_, index) => (
              <div
                key={index}
                className="absolute inset-y-0 border-s border-[#ebe7e1]"
                style={{ insetInlineStart: TIME_W + index * COL_W }}
              />
            ))}

            {BOOKINGS.map((booking, index) => (
              <BookingBlock
                key={booking.id}
                booking={booking}
                locale={locale}
                timeTo={copy.to}
                animate={{ opacity: filled ? 1 : 0, scale: filled ? 1 : 0.6 }}
                transition={
                  filled
                    ? { ...SPRING_SNAPPY, delay: 0.05 + index * 0.07 }
                    : { duration: 0.25 }
                }
              />
            ))}

            <BookingBlock
              booking={DRAGGED}
              locale={locale}
              timeTo={copy.to}
              className="z-10"
              animate={{
                opacity: filled ? 1 : 0,
                scale: step === STEP.drag ? 1.05 : filled ? 1 : 0.6,
                x: step >= STEP.drag ? dirSign * DRAG_COLUMNS * COL_W : 0,
                boxShadow:
                  step === STEP.grab || step === STEP.drag
                    ? '0 16px 30px -12px rgba(32,32,32,0.45), 0 0 0 2px #202020'
                    : '0 0 0 0 rgba(32,32,32,0)',
              }}
              transition={
                step === STEP.drag
                  ? { duration: 0.8, ease: EASE_OUT_EXPO }
                  : filled
                    ? { ...SPRING_SNAPPY, delay: step === STEP.fill ? 0.4 : 0 }
                    : { duration: 0.25 }
              }
            />

            <BookingBlock
              booking={WHATSAPP_BOOKING}
              locale={locale}
              timeTo={copy.to}
              badge={copy.via}
              className="z-10"
              animate={{
                opacity: step >= STEP.whatsapp ? 1 : 0,
                y: step >= STEP.whatsapp ? 0 : -14,
                boxShadow:
                  step === STEP.whatsapp
                    ? '0 0 0 4px rgba(169,200,255,0.75), 0 12px 28px -10px rgba(63,96,153,0.55)'
                    : '0 0 0 1px rgba(169,200,255,0.9), 0 0 0 0 rgba(0,0,0,0)',
              }}
              transition={{ duration: 0.6, ease: EASE_OUT_EXPO }}
            />

            <motion.div
              className="pointer-events-none absolute z-20 h-[1.5px] bg-[#2f7a63]"
              style={{ insetInlineStart: TIME_W - 4, insetInlineEnd: 0 }}
              initial={false}
              animate={{ top: NOW_TOP[step] }}
              transition={{
                duration: step === 0 ? 0.4 : DURATIONS[step]! / 1000,
                ease: 'linear',
              }}
            >
              <span className="absolute -top-[3px] start-0 h-[7.5px] w-[7.5px] rounded-full bg-[#2f7a63]" />
            </motion.div>

            <motion.span
              className="pointer-events-none absolute z-30 text-[#202020] drop-shadow-[0_2px_3px_rgba(0,0,0,0.25)]"
              style={{
                insetInlineStart: TIME_W + DRAGGED.court * COL_W + COL_W / 2,
                top: (DRAGGED.start - FIRST_HOUR) * HOUR + (DRAGGED.duration * HOUR) / 2,
              }}
              initial={false}
              animate={cursorState(step, dirSign)}
              transition={{ duration: step === STEP.drop ? 0.5 : 0.8, ease: EASE_OUT_EXPO, delay: step === STEP.drop ? 0.9 : 0 }}
            >
              <MousePointer2 className="h-4 w-4 fill-[#202020] stroke-white" strokeWidth={1.5} />
            </motion.span>
          </div>
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-3 z-40 flex justify-center">
        <motion.div
          className="flex items-center gap-2.5 whitespace-nowrap rounded-[12px] bg-[#202020] py-2 pe-4 ps-2.5 text-white shadow-[0_18px_40px_-14px_rgba(0,0,0,0.5)]"
          initial={false}
          animate={{ opacity: step === STEP.drop ? 1 : 0, y: step === STEP.drop ? 0 : 14 }}
          transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: step === STEP.drop ? 0.3 : 0 }}
        >
          <CheckBadge show={step === STEP.drop} size={20} bg={DEMO_COLORS.whatsapp} delay={0.45} />
          <span className="text-[11px] font-medium">{copy.moved}</span>
          <span className="h-3 w-px bg-white/25" />
          <span className="flex items-center gap-1 text-[10.5px] text-white/70">
            <MessageCircle className="h-3 w-3 text-[#25d366]" />
            {copy.notified}
          </span>
        </motion.div>
        </div>
      </AdminShell>
    </DemoStage>
  );
}

function cursorState(step: number, dirSign: number) {
  if (step < STEP.grab) {
    return { opacity: 0, x: dirSign * 150, y: 140 };
  }

  if (step === STEP.grab) {
    return { opacity: 1, x: 0, y: 0 };
  }

  if (step === STEP.drag) {
    return { opacity: 1, x: dirSign * DRAG_COLUMNS * COL_W, y: 0 };
  }

  return { opacity: 0, x: dirSign * (DRAG_COLUMNS * COL_W + 40), y: 40 };
}

function BookingBlock({
  booking,
  locale,
  timeTo,
  badge,
  className,
  animate,
  transition,
}: {
  booking: Booking;
  locale: DemoProps['locale'];
  timeTo: string;
  badge?: string;
  className?: string;
  animate: React.ComponentProps<typeof motion.div>['animate'];
  transition: React.ComponentProps<typeof motion.div>['transition'];
}) {
  const style = KIND_STYLE[booking.kind];
  const end = booking.start + booking.duration;

  return (
    <motion.div
      className={cn(
        'absolute flex flex-col overflow-hidden rounded-[8px] border-s-[3px] px-2 py-1.5',
        style.block,
        className,
      )}
      style={{
        insetInlineStart: TIME_W + booking.court * COL_W + 3,
        top: (booking.start - FIRST_HOUR) * HOUR + 2,
        width: COL_W - 6,
        height: booking.duration * HOUR - 4,
        borderInlineStartColor: style.bar,
        background: booking.kind === 'maintenance' ? HATCH : undefined,
      }}
      initial={false}
      animate={animate}
      transition={transition}
    >
      <span className="truncate text-[10.5px] font-semibold leading-tight">
        {booking.title[locale]}
      </span>
      <span className="mt-0.5 flex items-center justify-between gap-1 text-[9.5px] leading-tight">
        <span className="opacity-60">
          {clock(booking.start)} {timeTo} {clock(end)}
        </span>
        {booking.capacity && (
          <span className="shrink-0 font-roboto-mono text-[9px] opacity-80">
            {booking.capacity}
          </span>
        )}
      </span>
      {badge && (
        <span className="mt-auto flex w-fit items-center gap-1 whitespace-nowrap rounded-full bg-white px-1.5 py-[1px] text-[8.5px] font-medium text-[#1f7a4a]">
          <MessageCircle className="h-2.5 w-2.5" />
          {badge}
        </span>
      )}
    </motion.div>
  );
}

function Kpi({ label, value, suffix = '' }: { label: string; value: number; suffix?: string }) {
  return (
    <span className="flex h-full items-center gap-2 rounded-[10px] border border-[#ebe7e1] px-3">
      <span className="text-[10.5px] text-[#8a857c]">{label}</span>
      <span className="text-[13px] font-semibold text-[#202020]">
        <AnimatedNumber value={value} />
        {suffix}
      </span>
    </span>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}
